import { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { PiPencilSimpleLine, PiTrash } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import activeLoadStatusIcon from "../../../assets/images/Active_load.svg";
import fleetCostCardIcon from "../../../assets/images/Fleet_Cost.svg";
import inactiveStatusIcon from "../../../assets/images/Inactive_icon.svg";
import statusDecommissionedIcon from "../../../assets/images/Status Decommissioned.svg";
import statusUnderMaintenanceIcon from "../../../assets/images/Status Under Maintenance.svg";
import totalVehiclesCardIcon from "../../../assets/images/Total_Vehicles.svg";
import FilterExternalDropdown from "../../../components/FilterExternalDropdown";
import MultiLineText from "../../../components/MultiLineText";
import NewTableComponent from "../../../components/NewTableComponent";
import Stats from "../../../components/Stats";
import VehicleIdHoverCell from "../../../components/VehicleIdHoverCell";
import { fetchEquipmentDropdown } from "../../../store/Slices/EquipmentSlice";
import {
  deleteVehicle,
  fetchVehicleData,
  fetchVehiclesOverview,
} from "../../../store/Slices/ServiceSlice";
import { getUserDropdown } from "../../../store/Slices/UserSlice";
import {
  fetchVehicleCompanyBranchesDropdown,
  fetchVehicleMakesDropdown,
  fetchVehicleModelsDropdown,
  fetchVehicleTypesDropdown,
} from "../../../store/Slices/VehicleDropdownSlice";
import {
  AppMainModules,
  AssetsPermission,
  AssetsSubModules,
} from "../../../utils/app-constant";
import {
  findCollectionByKeys,
  hasPermission,
  normalizeOptions,
} from "../../../utils/helper";
import { activeStatsIcon, partUtilizedIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";
import AssetDeleteModal from "../AssetDeleteModal";

const toText = (value) => String(value ?? "").trim();

const toComparableNumber = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const resolvePositiveNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }

    const normalized = toText(value);
    const trailingDigits = normalized.match(/(\d+)$/);
    if (!trailingDigits) continue;

    const parsedTrailingDigits = Number(trailingDigits[1]);
    if (Number.isFinite(parsedTrailingDigits) && parsedTrailingDigits > 0) {
      return parsedTrailingDigits;
    }
  }

  return null;
};

const isNullishDisplayValue = (value) => {
  const normalized = toText(value).toLowerCase();
  return (
    !normalized ||
    normalized === "-" ||
    normalized === "--" ||
    normalized === "n/a" ||
    normalized === "null" ||
    normalized === "undefined" ||
    normalized.includes("nan") ||
    normalized.includes("null") ||
    normalized.includes("undefined") ||
    normalized === "eq-n/a" ||
    normalized === "eq--" ||
    normalized === "eq-"
  );
};

const uniqueMappedOptions = (rows = [], valueAccessor, labelAccessor) => {
  const map = new Map();

  rows.forEach((row) => {
    const rawValue =
      typeof valueAccessor === "function" ? valueAccessor(row) : "";
    const numericValue = resolvePositiveNumber(rawValue);
    const normalizedValue = numericValue ?? toText(rawValue);
    const uniqueKey = toText(normalizedValue);
    const label = toText(
      typeof labelAccessor === "function" ? labelAccessor(row) : "",
    );

    if (!uniqueKey || !label || map.has(uniqueKey)) return;

    map.set(uniqueKey, {
      label,
      value: numericValue ?? normalizedValue,
    });
  });

  return Array.from(map.values());
};

const hasFilterValue = (value) =>
  !(
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );

const buildSupportedSearchFilters = (filters = {}, rules = {}) => {
  const mapped = {};
  const entries = Object.keys(filters || {}).map((filterName) => [
    filterName,
    rules?.[filterName] || {},
  ]);

  entries.forEach(([filterName, rule = {}]) => {
    const filterObj = filters?.[filterName];
    const rawValue = filterObj?.value;

    if (!filterObj?.filter_type || !hasFilterValue(rawValue)) return;

    const normalizedType =
      typeof rule.normalizeType === "function"
        ? rule.normalizeType(filterObj.filter_type, rawValue)
        : filterObj.filter_type;

    if (!normalizedType) return;

    const normalizedValue =
      typeof rule.normalizeValue === "function"
        ? rule.normalizeValue(rawValue, filterObj.filter_type)
        : rawValue;

    if (!hasFilterValue(normalizedValue)) return;

    mapped[filterName] = {
      filter_type: normalizedType,
      value: normalizedValue,
    };
  });

  return mapped;
};

export const buildVehicleSearchFilters = (filters = {}) =>
  buildSupportedSearchFilters(filters);

const VEHICLE_STATUS_FILTER_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Under Maintenance", value: "under_maintenance" },
  { label: "Inactive", value: "inactive" },
  { label: "Decommissioned", value: "decommissioned" },
];

const VEHICLE_OWNERSHIP_FILTER_OPTIONS = [
  { label: "Owned", value: "owned" },
  { label: "Hired", value: "hired" },
];

const toCompactMillions = (value) => {
  const amount = Number(value) || 0;
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(2)} M`;
  return amount.toLocaleString("en-US");
};

const formatPercent = (part, total) => {
  if (!total) return "0.0%";
  return `${((part / total) * 100).toFixed(1)}%`;
};

const formatDateOnly = (value) => {
  if (!value) return "";
  const parsedDate = new Date(value);
  if (!Number.isFinite(parsedDate.getTime())) return "";

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatKm = (value) =>
  `${(toComparableNumber(value) || 0).toLocaleString("en-US")} km`;

const getDefaultTableLimit = () => 10;

const pickDisplayText = (...values) => {
  for (const value of values) {
    const normalized = toText(value);

    if (
      !normalized ||
      normalized === "-" ||
      normalized === "--" ||
      normalized === "N/A"
    ) {
      continue;
    }

    if (/^\d+$/.test(normalized)) {
      continue;
    }

    return normalized;
  }

  return "";
};



const findOptionLabelById = (options = [], id) => {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return "";

  const matchedOption = (Array.isArray(options) ? options : []).find(
    (option) => {
      const optionId = Number(option?.optionId ?? option?.id ?? option?.value);
      return Number.isFinite(optionId) && optionId === normalizedId;
    },
  );

  return toText(
    matchedOption?.label || matchedOption?.name || matchedOption?.title,
  );
};

const resolveVehicleEquipmentRecord = (row) => {
  if (row?.current_equipment && typeof row.current_equipment === "object") {
    return row.current_equipment;
  }

  if (
    row?.current_asset?.equipment &&
    typeof row.current_asset.equipment === "object"
  ) {
    return row.current_asset.equipment;
  }

  if (
    row?.current_asset &&
    typeof row.current_asset === "object" &&
    (toText(row.current_asset?.equipment_id) ||
      toText(row.current_asset?.serial_number) ||
      toText(row.current_asset?.systemId) ||
      toText(row.current_asset?.name))
  ) {
    return row.current_asset;
  }

  if (row?.equipment && typeof row.equipment === "object") {
    return row.equipment;
  }

  return null;
};

const resolveVehicleEquipmentLabel = (row) => {
  const equipment = resolveVehicleEquipmentRecord(row);
  const directLabel = pickDisplayText(
    equipment?.registration_number,
    equipment?.equipment_id,
    equipment?.systemId,
    equipment?.serial_number,
    equipment?.name,
    row?.current_asset?.equipment_id,
    row?.current_asset?.systemId,
    row?.current_asset?.serial_number,
    row?.current_asset?.name,
  );

  return directLabel || "N/A";
};

const resolveVehicleTyreLabels = (row) => {
  const tyreRows = Array.isArray(row?.tyres)
    ? row.tyres
    : Array.isArray(row?.current_tyres)
      ? row.current_tyres
      : [];

  return tyreRows
    .flatMap((axle) => {
      const directTyreLabel = toText(
        axle?.registration_number ||
          axle?.tyre?.registration_number ||
          axle?.tyres?.registration_number ||
          axle?.tyres_id ||
          axle?.systemId ||
          axle?.tyre_id ||
          axle?.serial_number,
      );

      if (directTyreLabel) {
        return [directTyreLabel];
      }

      return [
        axle?.outer_left_tyre_id,
        axle?.inner_left_tyre_id,
        axle?.inner_right_tyre_id,
        axle?.outer_right_tyre_id,
        axle?.left_tyre_id,
        axle?.right_tyre_id,
        axle?.left_inner_tyre_id,
        axle?.left_outer_tyre_id,
        axle?.right_inner_tyre_id,
        axle?.right_outer_tyre_id,
      ]
        .map((tyreId) => {
          const normalizedTyreId = toComparableNumber(tyreId);
          return normalizedTyreId ? `TY-${normalizedTyreId}` : "";
        })
        .filter(Boolean);
    })
    .filter(Boolean);
};

export const mapVehicleApiRow = (
  row,
  { makeOptions = [], modelOptions = [] } = {},
) => {
  const systemId = toText(row?.vehicle_id || row?.id);
  const linkedTyreLabels = resolveVehicleTyreLabels(row);
  const purchaseRecord = Array.isArray(row?.asset_purchases)
    ? row.asset_purchases[0] || {}
    : row?.asset_purchases || {};
  const currentOdometerValue = toComparableNumber(row?.odm) || 0;
  const lastLog =
    (row?.last_log && typeof row.last_log === "object" && row.last_log) ||
    (row?.lastLog && typeof row.lastLog === "object" && row.lastLog) ||
    (row?.current_location &&
      typeof row.current_location === "object" &&
      row.current_location) ||
    null;
  const lastKnownLatitude = toComparableNumber(
    lastLog?.lat ??
      lastLog?.latitude ??
      lastLog?.location_latitude ??
      row?.last_known_latitude ??
      row?.lastKnownLatitude ??
      row?.lat ??
      row?.latitude,
  );
  const lastKnownLongitude = toComparableNumber(
    lastLog?.lng ??
      lastLog?.longitude ??
      lastLog?.location_longitude ??
      row?.last_known_longitude ??
      row?.lastKnownLongitude ??
      row?.lng ??
      row?.longitude,
  );
  const lastKnownLocation = toText(
    lastLog?.location ??
      lastLog?.address ??
      lastLog?.location_point ??
      row?.current_location ??
      row?.last_known_location ??
      row?.lastKnownLocation,
  );
  const totalCost =
    toComparableNumber(purchaseRecord?.purchase_cost) ||
    toComparableNumber(row?.total_cost) ||
    toComparableNumber(row?.purchase_cost) ||
    0;
  const totalCostDisplay = toText(
    purchaseRecord?.purchase_cost ?? row?.total_cost ?? row?.purchase_cost,
  );
  const normalizedStatus =
    toText(row?.status)
      .toLowerCase()
      .replace(/[\s-]+/g, "_") || "inactive";
  const recordId = resolvePositiveNumber(row?.id, row?.vehicle_id, systemId);
  const makeId = resolvePositiveNumber(
    row?.make_id,
    row?.make?.id,
    row?.vehicle_make?.id,
  );
  const modelId = resolvePositiveNumber(
    row?.model_id,
    row?.model?.id,
    row?.vehicle_model?.id,
  );
  const vehicleTypeId = resolvePositiveNumber(
    row?.vehicle_type_id,
    row?.vehicle_type?.id,
    row?.type?.id,
  );
  const branchId = resolvePositiveNumber(row?.branch_id, row?.branch?.id);
  const driverId = resolvePositiveNumber(
    row?.assigned_driver?.id,
    row?.driver?.id,
    row?.driver_id,
  );
  const assignedDriverSource =
    (row?.assigned_driver &&
      typeof row.assigned_driver === "object" &&
      row.assigned_driver) ||
    (row?.driver && typeof row.driver === "object" && row.driver) ||
    {};
  const primaryDriver =
    row?.primary_driver && typeof row.primary_driver === "object"
      ? row.primary_driver
      : null;
  const secondaryDriver =
    row?.secondary_driver && typeof row.secondary_driver === "object"
      ? row.secondary_driver
      : null;
  const helper =
    row?.helper && typeof row.helper === "object" ? row.helper : null;
  const linkedEquipmentRecord = resolveVehicleEquipmentRecord(row);
  const equipmentId = resolvePositiveNumber(
    linkedEquipmentRecord?.id,
    linkedEquipmentRecord?.equipment_id,
    row?.equipment_id,
    row?.current_equipment_id,
    row?.current_asset?.id,
    row?.current_asset?.equipment?.id,
    row?.current_asset?.equipment_id,
    row?.current_asset?.source_id,
  );
  const make =
    pickDisplayText(
      row?.vehicle_make?.name,
      row?.vehicle_make?.title,
      row?.make?.name,
      row?.make_name,
      typeof row?.make === "string" ? row.make : "",
    ) ||
    findOptionLabelById(
      makeOptions,
      row?.make_id || row?.make?.id || row?.make,
    );
  const model =
    pickDisplayText(
      row?.vehicle_model?.name,
      row?.vehicle_model?.title,
      row?.model?.name,
      row?.model_name,
      typeof row?.model === "string" ? row.model : "",
    ) ||
    findOptionLabelById(
      modelOptions,
      row?.model_id || row?.model?.id || row?.model,
    );
  const vehicleType = pickDisplayText(
    row?.vehicle_type?.title,
    row?.vehicle_type?.name,
    row?.type?.title,
    row?.type?.name,
    row?.vehicle_type_name,
    row?.vehicleType,
  );
  const ownershipType = pickDisplayText(
    row?.ownership_type,
    row?.ownershipType,
  );
  const isHiredVehicle = String(ownershipType).toLowerCase() === "hired";
  const ownershipTypeLabel = isHiredVehicle
    ? "Hired"
    : ownershipType
    ? ownershipType
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "";
  const branch = toText(row?.branch?.name || row?.branch_name || row?.branch);
  const assignedDriverName = toText(
    row?.assigned_driver?.username ||
      row?.assigned_driver?.name ||
      row?.assigned_driver?.label ||
      row?.driver?.username ||
      row?.driver?.name ||
      row?.driver_name,
  );
  const year = toComparableNumber(row?.manufacturing_year);
  const tankCapacity = toComparableNumber(
    row?.tank_capacity ?? row?.tankCapacity,
  );
  const currentFuelEfficiency = toComparableNumber(
    row?.initial_fuel_efficiency ??
      row?.currentFuelEfficiency ??
      row?.current_fuel_efficiency ??
      row?.initialFuelEfficiency,
  );
  const nextServiceDue =
    formatDateOnly(row?.next_service_due || row?.nextServiceDue) || "-";

  return {
    ...row,
    id: row?.id,
    recordId,
    isCustom: false,
    systemId,
    vehicleId: systemId,
    registrationNumber:
      toText(row?.registration_number || row?.registrationNumber) || "-",
    imeino: toText(row?.imeino || row?.imei_no || row?.imeiNo) || "",
    vinChassisNo:
      toText(row?.vin || row?.chassis_number || row?.chassisNumber) || "-",
    makeId,
    make: make || "-",
    modelId,
    model: model || "-",
    vehicleTypeId,
    vehicleType: vehicleType || "-",
    ownershipType: ownershipTypeLabel || "-",
    ownershipTypeRaw: ownershipType || "",
    year: year && year > 0 ? year : "-",
    tankCapacity: tankCapacity !== null ? tankCapacity : "",
    currentFuelEfficiency:
      currentFuelEfficiency !== null ? currentFuelEfficiency : "-",
    branchId,
    branch: branch || "-",
    assignedDriver: {
      ...assignedDriverSource,
      id: driverId,
      username: assignedDriverName || "N/A",
      name: assignedDriverName || "N/A",
      image: row?.assigned_driver?.image || row?.driver?.image || "",
    },
    primaryDriver,
    secondaryDriver,
    helper,
    currentAsset: row?.current_asset || null,
    linkedEquipmentRecord,
    equipmentId,
    linkedEquipmentId: resolveVehicleEquipmentLabel(row),
    linkedTyres: linkedTyreLabels.length,
    linkedTyreLabels,
    currentOdometerValue,
    currentOdometer: formatKm(currentOdometerValue),
    current_fuel: row?.current_fuel || "0",
    lastLog,
    lastKnownLatitude,
    lastKnownLongitude,
    lastKnownLocation,
    nextServiceDue,
    status: normalizedStatus,
    totalCost,
    totalCostDisplay,
    description: row?.description || "",
    notes: row?.notes || "",
    formData: {
      registrationNumber:
        toText(row?.registration_number || row?.registrationNumber) || "",
      imeino: toText(row?.imeino || row?.imei_no || row?.imeiNo) || "",
      vinChassisNo:
        toText(row?.vin || row?.chassis_number || row?.chassisNumber) || "",
      make: make || "",
      model: model || "",
      vehicleType: vehicleType || "",
      vehicleTypeId,
      ownershipType: ownershipTypeLabel || "",
      ownershipTypeRaw: ownershipType || "",
      transporter:
        row?.transporter_id ||
        row?.transporter?.id ||
        row?.three_pl_transporter_id ||
        row?.threePlTransporter?.id ||
        "",
      transporterName:
        row?.transporter?.name ||
        row?.transporter?.label ||
        row?.transporter_name ||
        row?.threePlTransporter?.name ||
        "",
      loadCapacity: row?.load_capacity || row?.loadCapacity || "",
      driverName: row?.driver_name || row?.driverName || "",
      mobileNumber: row?.mobile_no || row?.mobileNo || row?.contact || "",
      cnic: row?.cnic || "",
      year: year && year > 0 ? year : "",
      tankCapacity: tankCapacity !== null ? tankCapacity : "",
      currentFuelEfficiency:
        currentFuelEfficiency !== null ? currentFuelEfficiency : "",
      branch: branch || "",
      status: normalizedStatus,
      description: row?.description || "",
      notes: row?.notes || "",
    },
  };
};

const statusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "active") {
    return {
      color: "#299900",
      label: "Active",
      icon: activeLoadStatusIcon,
    };
  }

  if (normalized === "under_maintenance") {
    return {
      color: "#F2841B",
      label: "Under Maintenance",
      icon: statusUnderMaintenanceIcon,
    };
  }

  if (normalized === "decommissioned") {
    return {
      color: "#0A53E8",
      label: "Decommissioned",
      icon: statusDecommissionedIcon,
    };
  }

  return {
    color: "#EF0000",
    label: "Inactive",
    icon: inactiveStatusIcon,
  };
};

const appendAssetsRegistrationTab = (path, tab = "vehicles") => {
  const normalizedTab = ["vehicles", "equipments", "tyres"].includes(
    String(tab || "")
      .trim()
      .toLowerCase(),
  )
    ? String(tab || "")
        .trim()
        .toLowerCase()
    : "vehicles";
  const separator = String(path).includes("?") ? "&" : "?";
  return `${path}${separator}tab=${normalizedTab}`;
};

const getVehicleBranchBadgeClassName = (branch) => {
  const normalizedBranch = String(branch || "")
    .trim()
    .toLowerCase();

  if (normalizedBranch.includes("holding")) {
    return "asset_branch_badge asset_branch_badge_holding";
  }

  return "asset_branch_badge asset_branch_badge_primary";
};

const getLinkedEquipmentLabel = (row) => {
  const directLabel = toText(
    row?.linkedEquipmentRecord?.registration_number ||
      row?.linkedEquipmentRecord?.equipment_id ||
      row?.linkedEquipmentRecord?.systemId ||
      row?.linkedEquipmentRecord?.serial_number ||
      row?.linkedEquipmentRecord?.name,
  );

  if (!isNullishDisplayValue(directLabel)) {
    return directLabel;
  }

  return "";
};

const getTrackingStatusMeta = (imeino) => {
  const hasImeiTrackerCode = !isNullishDisplayValue(toText(imeino));

  return hasImeiTrackerCode
    ? { label: "Connected", color: "#299900" }
    : { label: "Not Connected", color: "#ef0000" };
};

const AssetsVehiclesTab = ({
  activeTab = "vehicles",
  searchText: externalSearchText,
  setSearchText: externalSetSearchText,
  searchFilters: externalSearchFilters,
  setSearchFilters: externalSetSearchFilters,
  onExportStateChange,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canUpdateVehicle = hasPermission({
    permissionSlug: AssetsPermission.UpdateVehicle,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const canDeleteVehicle = hasPermission({
    permissionSlug: AssetsPermission.DeleteVehicle,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const {
    vehicleOverviewData = null,
    vehiclePaginatedData = [],
    vehiclePaginatedLoading = false,
    vehiclePaginatedError = null,
    deleteVehicleLoading = false,
    vehicleMeta = {},
  } = useSelector((state) => state.services);
  const {
    vehicleMakesDropdownData = [],
    vehicleModelsDropdownData = [],
    vehicleCompanyBranchesDropdownData = [],
    vehicleTypesDropdownData = [],
  } = useSelector((state) => state.vehicleDropdowns);
  const { equipmentDropdownData = [] } = useSelector(
    (state) => state.equipment,
  );
  const { userDropdownData = [] } = useSelector((state) => state.users || {});

  const [internalSearchText, setInternalSearchText] = useState("");
  const [internalSearchFilters, setInternalSearchFilters] = useState({});

  const searchText =
    externalSearchText !== undefined ? externalSearchText : internalSearchText;
  const setSearchText = externalSetSearchText || setInternalSearchText;
  const searchFilters =
    externalSearchFilters !== undefined
      ? externalSearchFilters
      : internalSearchFilters;
  const setSearchFilters = externalSetSearchFilters || setInternalSearchFilters;
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(getDefaultTableLimit());
  const [removedVehicleRows, setRemovedVehicleRows] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const vehicleApiSearchFilters = useMemo(
    () => buildVehicleSearchFilters(searchFilters),
    [searchFilters],
  );

  useEffect(() => {
    dispatch(fetchVehiclesOverview());
    dispatch(fetchVehicleCompanyBranchesDropdown());
    dispatch(fetchVehicleMakesDropdown());
    dispatch(fetchVehicleModelsDropdown());
    dispatch(fetchVehicleTypesDropdown());
    dispatch(fetchEquipmentDropdown());
    dispatch(getUserDropdown());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, searchFilters, limit]);

  useEffect(() => {
    dispatch(
      fetchVehicleData({
        page: currentPage,
        limit,
        search_text: searchText,
        searchFilters: vehicleApiSearchFilters,
      }),
    );
  }, [currentPage, dispatch, limit, searchText, vehicleApiSearchFilters]);

  const vehicleMakeDropdownOptions = useMemo(() => {
    const source = Array.isArray(vehicleMakesDropdownData)
      ? vehicleMakesDropdownData
      : findCollectionByKeys(vehicleMakesDropdownData, [
          "vehiclemakes",
          "makes",
          "vehiclemakesdropdown",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "make_id"],
      labelKeys: ["label", "make_name", "name", "title"],
      defaultLabelPrefix: "Make",
    });
  }, [vehicleMakesDropdownData]);

  const vehicleModelDropdownOptions = useMemo(() => {
    const source = Array.isArray(vehicleModelsDropdownData)
      ? vehicleModelsDropdownData
      : findCollectionByKeys(vehicleModelsDropdownData, [
          "vehiclemodels",
          "models",
          "vehiclemodelsdropdown",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "model_id"],
      labelKeys: ["label", "model_name", "name", "title"],
      defaultLabelPrefix: "Model",
    });
  }, [vehicleModelsDropdownData]);

  const vehicleTypeDropdownOptions = useMemo(() => {
    const source = Array.isArray(vehicleTypesDropdownData)
      ? vehicleTypesDropdownData
      : findCollectionByKeys(vehicleTypesDropdownData, [
          "vehicletypes",
          "types",
          "vehicletypesdropdown",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "vehicle_type_id"],
      labelKeys: ["label", "title", "name"],
      defaultLabelPrefix: "Type",
    });
  }, [vehicleTypesDropdownData]);

  const vehicleBranchDropdownOptions = useMemo(() => {
    const source = Array.isArray(vehicleCompanyBranchesDropdownData)
      ? vehicleCompanyBranchesDropdownData
      : findCollectionByKeys(vehicleCompanyBranchesDropdownData, [
          "companybranches",
          "branches",
          "vehiclecompanybranchesdropdown",
          "branchdropdown",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "branch_id"],
      labelKeys: ["label", "branch_name", "name", "title"],
      defaultLabelPrefix: "Branch",
    });
  }, [vehicleCompanyBranchesDropdownData]);

  const vehicleDriverDropdownOptions = useMemo(
    () =>
      (Array.isArray(userDropdownData) ? userDropdownData : [])
        .map((item) => ({
          label: toText(item?.label || item?.name || item?.title),
          value: resolvePositiveNumber(item?.value, item?.id),
        }))
        .filter((item) => item.label && item.value !== null),
    [userDropdownData],
  );

  const equipmentDropdownOptions = useMemo(() => {
    const source = Array.isArray(equipmentDropdownData)
      ? equipmentDropdownData
      : findCollectionByKeys(equipmentDropdownData, [
          "equipments",
          "equipmentdropdown",
          "equipment",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "equipment_id"],
      labelKeys: ["label", "equipment_id", "title", "name", "serial_number"],
      defaultLabelPrefix: "Equipment",
    });
  }, [equipmentDropdownData]);

  const mappedVehicleRows = useMemo(
    () =>
      (Array.isArray(vehiclePaginatedData) ? vehiclePaginatedData : [])
        .map((row) => {
          return mapVehicleApiRow(row, {
            makeOptions: vehicleMakeDropdownOptions,
            modelOptions: vehicleModelDropdownOptions,
          });
        })
        .filter(
          (row) => row.systemId && !removedVehicleRows.includes(row.systemId),
        ),
    [
      removedVehicleRows,
      vehicleMakeDropdownOptions,
      vehicleModelDropdownOptions,
      vehiclePaginatedData,
    ],
  );

  const allVehicleRows = useMemo(() => {
    const bySystemId = new Map();

    mappedVehicleRows.forEach((row) => {
      const key = String(row?.systemId || "").trim();
      if (!key || removedVehicleRows.includes(key)) return;
      bySystemId.set(key, row);
    });

    return Array.from(bySystemId.values());
  }, [mappedVehicleRows, removedVehicleRows]);

  const totalPages = Math.max(
    1,
    Number(vehicleMeta?.totalPages) ||
      Number(vehicleMeta?.pages) ||
      Number(vehicleMeta?.lastPage) ||
      1,
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    onExportStateChange?.({
      searchText,
      searchFilters,
      vehicleApiSearchFilters,
      totalItems: vehicleMeta?.totalItems || 0,
      makeOptions: vehicleMakeDropdownOptions,
      modelOptions: vehicleModelDropdownOptions,
      rows: allVehicleRows,
    });
  }, [
    allVehicleRows,
    onExportStateChange,
    searchFilters,
    searchText,
    vehicleApiSearchFilters,
    vehicleMeta?.totalItems,
    vehicleMakeDropdownOptions,
    vehicleModelDropdownOptions,
  ]);

  const vehicleStats = useMemo(() => {
    const hasApiOverview =
      vehicleOverviewData && typeof vehicleOverviewData === "object";
    const totalVehicles = hasApiOverview
      ? Number(vehicleOverviewData?.total_vehicles) || 0
      : allVehicleRows.length;
    const activeVehicles = hasApiOverview
      ? Number(vehicleOverviewData?.active) || 0
      : allVehicleRows.filter((row) => row.status === "active").length;
    const underMaintenanceVehicles = hasApiOverview
      ? Number(vehicleOverviewData?.under_maintenance) || 0
      : allVehicleRows.filter((row) => row.status === "under_maintenance")
          .length;
    const fleetCost = hasApiOverview
      ? Number(vehicleOverviewData?.total_fleet_cost) || 0
      : allVehicleRows.reduce(
          (sum, row) => sum + (Number(row?.totalCost) || 0),
          0,
        );

    return {
      totalVehicles,
      activeVehicles,
      underMaintenanceVehicles,
      fleetCost,
      activePercentage: formatPercent(activeVehicles, totalVehicles),
      underMaintenancePercentage: formatPercent(
        underMaintenanceVehicles,
        totalVehicles,
      ),
    };
  }, [allVehicleRows, vehicleOverviewData]);

  const statsData = useMemo(
    () => [
      {
        title: "Total Vehicles",
        value: vehicleStats.totalVehicles,
        text: <>Last updated : Feb 22, 2026</>,
        icon: (
          <img
            src={totalVehiclesCardIcon}
            alt="Total vehicles"
            className="asset_registration_stats_icon"
          />
        ),
      },
      {
        title: "Active",
        value: vehicleStats.activeVehicles,
        text: (
          <span>
            <b>{vehicleStats.activePercentage}</b> from total
          </span>
        ),
        icon: activeStatsIcon,
        className: "completed",
      },
      {
        title: "Under Maintenance",
        value: vehicleStats.underMaintenanceVehicles,
        text: (
          <span>
            <b>{vehicleStats.underMaintenancePercentage}</b> from total
          </span>
        ),
        icon: partUtilizedIcon,
        className: "late",
      },
      {
        title: "Total Fleet Cost (PKR)",
        value: toCompactMillions(vehicleStats.fleetCost),
        text: (
          <span>
            <b>100%</b> of vehicle asset value
          </span>
        ),
        icon: (
          <img
            src={fleetCostCardIcon}
            alt="Total fleet cost"
            className="asset_registration_stats_icon"
          />
        ),
        className: "pending-delivery",
      },
    ],
    [vehicleStats],
  );

  const vehicleOptionSets = useMemo(
    () => ({
      makeOptions: vehicleMakeDropdownOptions.length
        ? vehicleMakeDropdownOptions
        : uniqueMappedOptions(
            allVehicleRows,
            (row) => row.makeId,
            (row) => row.make,
          ),
      modelOptions: vehicleModelDropdownOptions.length
        ? vehicleModelDropdownOptions
        : uniqueMappedOptions(
            allVehicleRows,
            (row) => row.modelId,
            (row) => row.model,
          ),
      vehicleTypeOptions: vehicleTypeDropdownOptions.length
        ? vehicleTypeDropdownOptions
        : uniqueMappedOptions(
            allVehicleRows,
            (row) => row.vehicleTypeId || row.vehicleType,
            (row) => row.vehicleType,
          ),
      ownershipTypeOptions: VEHICLE_OWNERSHIP_FILTER_OPTIONS,
      branchOptions: vehicleBranchDropdownOptions.length
        ? vehicleBranchDropdownOptions
        : uniqueMappedOptions(
            allVehicleRows,
            (row) => row.branchId,
            (row) => row.branch,
          ),
      driverOptions: vehicleDriverDropdownOptions.length
        ? vehicleDriverDropdownOptions
        : uniqueMappedOptions(
            allVehicleRows,
            (row) => row?.assignedDriver?.id,
            (row) => row?.assignedDriver?.name,
          ),
      linkedEquipmentOptions: equipmentDropdownOptions.length
        ? equipmentDropdownOptions
        : uniqueMappedOptions(
            allVehicleRows,
            (row) => row.equipmentId,
            (row) => row.linkedEquipmentId,
          ),
      statusOptions: VEHICLE_STATUS_FILTER_OPTIONS,
    }),
    [
      allVehicleRows,
      equipmentDropdownOptions,
      vehicleBranchDropdownOptions,
      vehicleDriverDropdownOptions,
      vehicleMakeDropdownOptions,
      vehicleModelDropdownOptions,
      vehicleTypeDropdownOptions,
    ],
  );

  const handleDeleteRow = async (row) => {
    if (Number(row?.id) > 0) {
      try {
        await dispatch(deleteVehicle(row.id)).unwrap();
        successNotify("Vehicle deleted successfully");
        dispatch(fetchVehiclesOverview());

        if (allVehicleRows.length === 1 && currentPage > 1) {
          setCurrentPage((previous) => Math.max(1, previous - 1));
        } else {
          dispatch(
            fetchVehicleData({
              page: currentPage,
              limit,
              search_text: searchText,
              searchFilters: vehicleApiSearchFilters,
            }),
          );
        }
      } catch (error) {
        errorNotify(error);
        return false;
      }

      return true;
    }

    setRemovedVehicleRows((previous) =>
      previous.includes(row.systemId) ? previous : [...previous, row.systemId],
    );
    return true;
  };

  const openDeleteModal = (row) => {
    setDeleteTarget(row);
  };

  const closeDeleteModal = () => {
    if (deleteVehicleLoading) return;
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const didDelete = await handleDeleteRow(deleteTarget);
    if (didDelete) {
      setDeleteTarget(null);
    }
  };

  const navigateToLinkedEquipment = (row) => {
    const routeId = row?.equipmentId || getLinkedEquipmentLabel(row);
    if (!routeId || isNullishDisplayValue(routeId)) return;

    navigate(
      appendAssetsRegistrationTab(
        `/assets/equipments/${encodeURIComponent(routeId)}`,
        activeTab,
      ),
      {
        state: {
          equipment: {
            id: row?.equipmentId || row?.linkedEquipmentRecord?.id || null,
            systemId: row?.linkedEquipmentId || "",
            equipmentId: row?.linkedEquipmentId || "",
            serialNumber:
              row?.linkedEquipmentRecord?.serial_number ||
              row?.linkedEquipmentId ||
              "",
            registrationNumber:
              row?.linkedEquipmentRecord?.registration_number ||
              row?.linkedEquipmentRecord?.name ||
              row?.linkedEquipmentId ||
              "",
            status: row?.linkedEquipmentRecord?.status || "",
            formData: {
              assetNumber: row?.linkedEquipmentRecord?.asset_number || "",
              serialNumber:
                row?.linkedEquipmentRecord?.serial_number ||
                row?.linkedEquipmentId ||
                "",
              registrationNumber:
                row?.linkedEquipmentRecord?.registration_number ||
                row?.linkedEquipmentRecord?.name ||
                row?.linkedEquipmentId ||
                "",
              status: row?.linkedEquipmentRecord?.status || "",
            },
          },
          activeTab,
        },
      },
    );
  };

  const commonColumns = {
    checked: true,
    disabled: true,
    hide: false,
    exportColumn: true,
    sortable: false,
  };

  const columns = [
    {
      ...commonColumns,
      name: "Vehicle Id",
      selector: (row, index) => (
        <VehicleIdHoverCell
          row={row}
          index={index}
          onClick={() =>
            navigate(
              appendAssetsRegistrationTab(
                `/assets/vehicles/${encodeURIComponent(row.vehicleId)}`,
                activeTab,
              ),
              {
                state: { vehicle: row, activeTab },
              },
            )
          }
        />
      ),
      tableColumn: false,
      filterName: "vehicleIdFilter",
      type: "text",
      minWidth: "120px",
    },
    {
      ...commonColumns,
      name: "Registration Number",
      selector: (row) => row.registrationNumber,
      tableColumn: true,
      filterName: "registrationNumberFilter",
      type: "text",
      minWidth: "170px",
    },
    {
      ...commonColumns,
      name: "Type",
      selector: (row) => row.vehicleType || "-",
      tableColumn: true,
      filterName: "vehicleTypeFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: vehicleOptionSets.vehicleTypeOptions,
      minWidth: "160px",
    },

    {
      ...commonColumns,
      name: "Status",
      selector: (row) => {
        const meta = statusMeta(row.status);

        return (
          <span className="asset_status_cell" style={{ color: meta.color }}>
            <img
              src={meta.icon}
              alt={meta.label}
              className="asset_status_icon"
            />
            {meta.label}
          </span>
        );
      },
      tableColumn: true,
      filterName: "statusFilter",
      minWidth: "140px",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: vehicleOptionSets.statusOptions,
    },
    {
      ...commonColumns,
      name: "Current Fuel (Ltr)",
      selector: (row) => row?.current_fuel || "0",
      tableColumn: true,
      filterName: "currentFuelFilter",
      type: "number",
      minWidth: "170px",
    },
    {
      ...commonColumns,
      name: "Tank Capacity (Ltr)",
      selector: (row) => row?.tankCapacity || "0",
      tableColumn: true,
      filterName: "tankCapacityFilter",
      type: "number",
      minWidth: "180px",
    },
    {
      ...commonColumns,
      name: "Current Fuel Efficiency",
      selector: (row) =>
        row?.currentFuelEfficiency !== "-" &&
        row?.currentFuelEfficiency !== undefined &&
        row?.currentFuelEfficiency !== null &&
        row?.currentFuelEfficiency !== ""
          ? `${row.currentFuelEfficiency} km/l`
          : "-",
      tableColumn: false,
      exportColumn: true,
      minWidth: "190px",
    },
    {
      ...commonColumns,
      name: "Current Odometer",
      selector: (row) => row.currentOdometer,
      tableColumn: true,
      filterName: "odometerFilter",
      type: "number",
      minWidth: "160px",
    },

    {
      ...commonColumns,
      name: "Make",
      selector: (row) => row.make,
      tableColumn: false,
      filterName: "makeFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: vehicleOptionSets.makeOptions,
      minWidth: "95px",
    },
    {
      ...commonColumns,
      name: "Model",
      selector: (row) => row.model,
      tableColumn: false,
      filterName: "modelFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: vehicleOptionSets.modelOptions,
      minWidth: "145px",
    },

    {
      ...commonColumns,
      name: "Ownership",
      selector: (row) => {
        const type = String(row.ownershipTypeRaw || row.ownershipType || "")
          .trim()
          .toLowerCase();
        if (type === "hired") {
          return (
            <span className="asset_branch_badge asset_ownership_badge_hired">
              Hired
            </span>
          );
        }
        if (type === "owned" || type === "purchased") {
          return (
            <span className="asset_branch_badge asset_ownership_badge_owned">
              Owned
            </span>
          );
        }
        return row.ownershipType || "-";
      },
      tableColumn: true,
      filterName: "ownershipTypeFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: vehicleOptionSets.ownershipTypeOptions,
      minWidth: "130px",
    },
    {
      ...commonColumns,
      name: "Year",
      selector: (row) => row.year,
      tableColumn: false,
      filterName: "yearFilter",
      type: "number",
      minWidth: "90px",
    },
    {
      ...commonColumns,
      name: "IMEI Tracker Code",
      selector: (row) => row.imeino || "-",
      tableColumn: false,
      minWidth: "200px",
    },
    {
      ...commonColumns,
      name: "Branch",
      selector: (row) => (
        <span className={getVehicleBranchBadgeClassName(row.branch)}>
          {row.branch || "-"}
        </span>
      ),
      tableColumn: false,
      filterName: "branchFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: vehicleOptionSets.branchOptions,
      minWidth: "170px",
    },
    {
      ...commonColumns,
      name: "Assigned Driver",
      selector: (row) => {
        const driversList = [];

        if (row?.primaryDriver) {
          const name = row.primaryDriver.username || row.primaryDriver.name;
          if (name && !isNullishDisplayValue(name)) {
            driversList.push(`${name} (Primary)`);
          }
        }
        if (row?.secondaryDriver) {
          const name = row.secondaryDriver.username || row.secondaryDriver.name;
          if (name && !isNullishDisplayValue(name)) {
            driversList.push(`${name} (Secondary)`);
          }
        }
        if (row?.helper) {
          const name = row.helper.username || row.helper.name;
          if (name && !isNullishDisplayValue(name)) {
            driversList.push(`${name} (Helper)`);
          }
        }

        // Fallback to assignedDriver if no primary, secondary or helper is found
        if (driversList.length === 0 && row?.assignedDriver) {
          const name = row.assignedDriver.username || row.assignedDriver.name;
          if (name && !isNullishDisplayValue(name)) {
            driversList.push(name);
          }
        }

        if (driversList.length === 0) {
          return <span style={{ color: "#9ca3af" }}>-</span>;
        }

        if (driversList.length === 1) {
          return (
            <span className="service-task" style={{ display: "inline-block" }}>
              <MultiLineText text={driversList[0]} maxWidth={"200px"} />
            </span>
          );
        }

        const firstItem = driversList[0];
        const remainingCount = driversList.length - 1;

        return (
          <div className="service-tasks-container">
            <span className="first-task">
              <MultiLineText text={firstItem} maxWidth={"200px"} />
            </span>

            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <div>
                    {driversList.slice(1).map((item, index) => (
                      <div key={index} className="tooltip_item">
                        {item}
                      </div>
                    ))}
                  </div>
                </Tooltip>
              }
            >
              <span className="additional-tasks">+{remainingCount}</span>
            </OverlayTrigger>
          </div>
        );
      },
      tableColumn: false,
      filterName: "driverFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: vehicleOptionSets.driverOptions,
      minWidth: "280px",
      width: "280px",
    },
    {
      ...commonColumns,
      name: "Linked Equipment ID",
      selector: (row) => {
        const linkedEquipmentLabel = getLinkedEquipmentLabel(row);

        if (!isNullishDisplayValue(linkedEquipmentLabel)) {
          return (
            <div
              className="service-tasks-container"
              onClick={(event) => {
                event.stopPropagation();
                navigateToLinkedEquipment(row);
              }}
              style={{ cursor: "pointer" }}
            >
              <span className="first-task">
                <MultiLineText text={linkedEquipmentLabel} maxWidth={"90px"} />
              </span>
            </div>
          );
        }

        return <span style={{ color: "#9ca3af" }}>-</span>;
      },
      tableColumn: false,
      filterName: "equipmentFilter",
      type: "text",
      filterComponent: FilterExternalDropdown,
      isMulti: true,
      options: vehicleOptionSets.linkedEquipmentOptions,
      minWidth: "220px",
    },
    {
      ...commonColumns,
      name: "Linked Tyres",
      selector: (row) => {
        const serviceItems = Array.isArray(row.linkedTyreLabels)
          ? row.linkedTyreLabels
          : [];
        const count = serviceItems.length || Number(row.linkedTyres) || 0;

        if (count === 0 || !serviceItems.length) {
          return <span style={{ color: "#9ca3af" }}>-</span>;
        }

        if (serviceItems.length === 1) {
          return (
            <span className="service-task">
              <MultiLineText text={serviceItems[0]} maxWidth={"90px"} />
            </span>
          );
        }

        const firstItem = serviceItems[0];
        const remainingCount = serviceItems.length - 1;

        return (
          <div className="service-tasks-container">
            <span className="first-task">
              <MultiLineText text={firstItem} maxWidth={"90px"} />
            </span>

            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <div>
                    {serviceItems.slice(1).map((item, index) => (
                      <div key={index} className="tooltip_item">
                        {item}
                      </div>
                    ))}
                  </div>
                </Tooltip>
              }
            >
              <span className="additional-tasks">+{remainingCount}</span>
            </OverlayTrigger>
          </div>
        );
      },
      tableColumn: false,
      minWidth: "150px",
    },
    {
      ...commonColumns,
      name: "Next Service Due",
      selector: (row) => row.nextServiceDue,
      tableColumn: false,
      minWidth: "150px",
    },
    {
      ...commonColumns,
      name: "Tracking Status",
      selector: (row) => {
        const trackingStatus = getTrackingStatusMeta(row?.imeino);

        return (
          <span
            className="asset_status_cell"
            style={{ color: trackingStatus.color }}
          >
            {trackingStatus.label}
          </span>
        );
      },
      tableColumn: false,
      minWidth: "160px",
    },

    {
      ...commonColumns,
      name: "Total Cost (PKR)",
      selector: (row) =>
        row.totalCostDisplay ||
        Number(row.totalCost || 0).toLocaleString("en-US"),
      tableColumn: false,
      minWidth: "145px",
    },
    {
      name: "Action",
      checked: true,
      disabled: false,
      hide: false,
      exportColumn: false,
      sortable: false,
      selector: (row) => (
        <div className="table_action_btn">
          {canUpdateVehicle && (
            <button
              type="button"
              className="edit_btn"
              title="Edit"
              onClick={() =>
                navigate(
                  appendAssetsRegistrationTab(
                    `/assets/vehicles/${encodeURIComponent(row.vehicleId)}`,
                    activeTab,
                  ),
                  {
                    state: { vehicle: row, activeTab },
                  },
                )
              }
            >
              <PiPencilSimpleLine />
            </button>
          )}
          {canDeleteVehicle && (
            <button
              type="button"
              className="delete_btn"
              title="Delete"
              onClick={() => openDeleteModal(row)}
            >
              <PiTrash />
            </button>
          )}
        </div>
      ),
      tableColumn: false,
      minWidth: "100px",
      grow: 0,
    },
  ];

  return (
    <>
      <AssetDeleteModal
        show={Boolean(deleteTarget)}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        loading={deleteVehicleLoading}
        heading="Delete Vehicle"
        message="Are you sure you want to delete this vehicle?"
      />

      <Stats statsData={statsData} />

      <NewTableComponent
        showAddBtn={false}
        columns={columns}
        data={allVehicleRows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={
          Number(vehicleMeta?.totalItems) ||
          Number(vehicleMeta?.total_records) ||
          Number(vehicleMeta?.count) ||
          allVehicleRows.length
        }
        totalPages={totalPages}
        limitOptions={[10, 20, 50, 100]}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={vehiclePaginatedLoading || deleteVehicleLoading}
        paginationLoading={vehiclePaginatedLoading || deleteVehicleLoading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        tableClassName="assets_registration_table assets_registration_table_vehicle"
        noDataComponent={
          <div className="table_no_records">
            {vehiclePaginatedError || "There are no records to display"}
          </div>
        }
      />
    </>
  );
};

export default AssetsVehiclesTab;
